'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import Link from '@/components/atoms/link';
import useClassNames from './hooks/useClassNames';
import { Reference } from '@/types/lib/reference';
import Image from '@/components/atoms/Image';
import Column from './atoms/column';
import FooterBottom from './atoms/footerBottom';
import { desktop } from '@/constants/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';

// Import the logo images
import logo1 from './Logos/logo1.png';
import logo2 from './Logos/logo2.png';
import logo3 from './Logos/logo3.png';
import logo4 from './Logos/logo4.png';
import logo5 from './Logos/logo5.png';
import logo6 from './Logos/logo6.png';
import logo7 from './Logos/logo7.png';
import logo8 from './Logos/logo8.png';
import useAccount from '@/lib/hooks/useAccount';

export interface FooterProps {
  links?: FooterLink[];
  copyrightStatement?: string;
  variant?: string;
  logo?: string;
  columns?: FooterColumn[];
  socialMedia?: any;
}

export interface FooterLink {
  name: string;
  link?: string;
  reference: Reference;
}
export interface FooterColumn {
  label: string;
  link: FooterLink[];
  open_in_new_tab: boolean;
}
const Footer = ({ links = [], copyrightStatement, variant = 'default', logo = '', columns = [] }: FooterProps) => {
  const pathname = usePathname();
  const isCreateProfilePage = pathname?.includes('/create-profile');
  const [isDesktopSize] = useMediaQuery(desktop);
  const { loggedIn } = useAccount();
const isNZLocale = pathname?.startsWith('/nz');
  const newLogoData = [
    {
      logo: logo1,
      link: '/caroma',
      height: isDesktopSize?30:23,
      width: isDesktopSize?193:149
    },
    {
      logo: logo2,
      link: '/methven',
      height: isDesktopSize?30:23,
      width: isDesktopSize?160:123
    },
    {
      logo: logo3,
      link: '/dorf',
      height:isDesktopSize? 33:23,
      width: isDesktopSize?68:48
    },
    {
      logo: logo4,
      link: '/clark',
      height: isDesktopSize?30:24,
      width: isDesktopSize? 170:133
    },
    {
      logo: logo5,
      link: '/stylus',
      height: isDesktopSize?45:40,
      width: isDesktopSize?90:80
    },
    {
      logo: logo7,
      link: '/leak-smartshield',
      height: isDesktopSize?45:40,
      width: isDesktopSize?143:119.5
    },
    {
      logo: logo8,
      link: '/tradeready',
      height: isDesktopSize?45:40,
      width: isDesktopSize?90:80
      
    }
  ].filter((item) => !(isNZLocale && item.link === '/leak-smartshield'));


  return (
    <footer
      aria-labelledby="footer-heading"
      className="w-full bg-[#090909] py-6"
    >
      <div className="max-w-[1920px] mx-auto px-4 lg:px-10">
      <div className="hidden gap-8 lg:flex items-baseline">
        {newLogoData.map((logo, index) => (
          isCreateProfilePage || !loggedIn ? (
            <div key={index}>
              <Image
                src={logo.logo.src}
                width={logo.width}
                height={logo.height}
                alt="logo"
                style={{ height: logo.height, width: logo.width }}
              />
            </div>
          ) : (
            <Link link={`/product${logo.link}`} key={index}>
              <Image
                src={logo.logo.src}
                width={logo.width}
                height={logo.height}
                alt="logo"
                style={{ height: logo.height, width: logo.width }}
              />
            </Link>
          )
        ))}
      </div>
      <ul className="flex flex-col items-center lg:hidden">
        <div className="mb-[36px] grid grid-cols-2 gap-6 items-center justify-items-center">
          {newLogoData.map((logo, index) => (
            isCreateProfilePage || !loggedIn ? (
              <div key={index} className="flex items-center justify-center w-[130px] h-[40px]">
                <Image
                  src={logo.logo.src}
                  width={logo.width}
                  height={logo.height}
                  alt="logo"
                  style={{ height: '100%', width: 'auto', maxWidth: '130px', objectFit: 'contain' }}
                />
              </div>
            ) : (
              <Link link={`/product${logo.link}`} key={index} className="flex items-center justify-center w-[130px] h-[40px]">
                <Image
                  src={logo.logo.src}
                  width={logo.width}
                  height={logo.height}
                  alt="logo"
                  style={{ height: '100%', width: 'auto', maxWidth: '130px', objectFit: 'contain' }}
                />
              </Link>
            )
          ))}
        </div>
        {columns.map((column, index) => (
          <li key={index} className={`column-border flex flex-col items-center`}>
            <Column
              header={column?.label}
              link={column?.link ?? []}
              openInNewTab={column?.open_in_new_tab}
              className="pb-4 pt-4 text-white"
            />
          </li>
        ))}
        <div className={`mt-8 ${!isCreateProfilePage ? 'mb-10 lg:mb-0' : ''}`}>
          <FooterBottom copyright={copyrightStatement} />
        </div>
      </ul>

      <div className="hidden w-full justify-between lg:flex mt-[40px]">
        <FooterBottom copyright={copyrightStatement} />
        <div className={`flex flex-row items-center`}>
          {columns.map((column, index) => (
            <div
              key={index}
              className={`flex flex-row items-center  justify-between ${index === columns.length - 1 ? '' : 'column-with-separator'
                }`}
            >
              <Column
                header={column.label}
                link={column?.link ?? []}
                openInNewTab={column?.open_in_new_tab}
                className="!text-[13px]"
                textSize={13}
              />
            </div>
          ))}
        </div>
      </div>
      </div>
    </footer>
  );
};

export default Footer;
